/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {image} - props.image - Image.
 * @param {string} - props.text_headline - Text Overlay.
 * * @param {string} - props.text_alignment - Text Overlay.
 * @returns {React.ReactElement} - ImageWithText component.
 */
export const CategoryTile = ({ITCLink, custom, text_headline, text_alignment}) => {
    const segment = useSegment()
    const hasCaption = text_headline
    const {site, locale} = useMultiSite()
    const categoryUrl = getPDPath(ITCLink, site, locale)

    return (
        <Box className={'image-with-text'}>
            <Box
                as="figure"
                className={'image-with-text-figure'}
                onClick={(event) => {
                    // segment
                    segment.sendPanelSelectedEvent(event, {}, {})
                }}
            >
                <Link to={categoryUrl}>
                    <picture>
                        <source srcSet={custom?.image?.disBaseLink} media="(min-width: 48em)" />
                        <source srcSet={custom?.image?.disBaseLink} media="(min-width: 64em)" />
                        <img
                            src={custom?.image?.disBaseLink}
                            alt={custom?.image?.disBaseLink}
                            loading="lazy"
                        />
                    </picture>
                </Link>
            </Box>
            {hasCaption && (
                <Text as="figcaption">
                    {text_headline && (
                        <Box className={'image-with-text-heading-container'}>
                            <a href={categoryUrl?.url}>
                                <Text
                                    as="span"
                                    className={'image-with-text-heading-text'}
                                    color="black"
                                    textAlign={text_alignment ? 'center' : ''}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: text_headline
                                        }}
                                    />
                                </Text>
                            </a>
                        </Box>
                    )}
                </Text>
            )}
        </Box>
    )
}

CategoryTile.propTypes = {
    ITCLink: PropTypes.string,
    custom: PropTypes.object,
    text_headline: PropTypes.string,
    category: PropTypes.string,
    text_alignment: PropTypes.string
}

export default CategoryTile
